import React from "react";
import "../styles/shared-ui.css";
import "../styles/job-detail.css";
import { useAppContext } from "../context/AppContext";
import { getCompanyNames } from "../services/companyService";
import { getJobsByCompanyId } from "../services/jobService";
import { getTaskWithRelations, updateTask } from "../services/taskService";
import { getTaskCategories } from "../services/taskCategoryService";
import { getTaskStatuses } from "../services/taskStatusService";
import SideTabs from "../components/layout/SideTabs";
import RichTextEditor from "../components/ui/RichTextEditor";
import SkillTable from "../components/ui/SkillTable";
import SkillAddModal from "../components/ui/SkillAddModal";
import RaciMatrix, { getRaciValidation } from "../components/ui/RaciMatrix";
import { LEVELS } from "../components/ui/levels";
import { toast } from "../utils/toast";
import { useLocation, useParams } from "react-router-dom";

const FREQUENCY_PERIODS = ["DAY", "WEEK", "MONTH", "YEAR"];

const describeRecurrence = (occurrences, frequencyInterval, frequencyPeriod) => {
  const occ = Number(occurrences);
  const interval = Number(frequencyInterval);
  if (
    !Number.isFinite(occ) ||
    !Number.isFinite(interval) ||
    occ <= 0 ||
    interval <= 0
  ) {
    return "";
  }
  const periodLabel = String(frequencyPeriod || "WEEK").toLowerCase();
  const periodText = interval === 1 ? periodLabel : `${periodLabel}s`;
  const timesText = occ === 1 ? "time" : "times";
  return `${occ} ${timesText} in ${interval} ${periodText}`;
};

const TaskEditPage = () => {
  const { setActiveSection } = useAppContext();
  const { taskId: taskIdParam } = useParams();
  const location = useLocation();
  const routeTaskId = React.useMemo(() => {
    if (taskIdParam != null && taskIdParam !== "") {
      try {
        return decodeURIComponent(taskIdParam);
      } catch {
        return taskIdParam;
      }
    }
    const pathname = String(location?.pathname || "");
    const match = pathname.match(/^\/tasks\/([^/]+)\/edit$/i);
    if (!match?.[1]) return null;
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }, [taskIdParam, location?.pathname]);

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const [companies, setCompanies] = React.useState([]);
  const [allJobs, setAllJobs] = React.useState([]);
  const [raciRows, setRaciRows] = React.useState([]);
  const [taskCategories, setTaskCategories] = React.useState([]);
  const [taskStatuses, setTaskStatuses] = React.useState([]);

  const [data, setData] = React.useState(null);
  const [form, setForm] = React.useState({
    task_name: "",
    task_code: "",
    version: "",
    task_company_id: "",
    expected_process_time: "",
    optimistic_process_time: "",
    pessimistic_process_time: "",
    expected_waiting_time: "",
    expected_rework_time: "",
    status_id: "",
    category_id: "",
    task_overview: "",
    frequency_period: "WEEK",
    frequency_interval: "1",
    occurrences: "1",
    skills: [],
  });

  const [showAddSkill, setShowAddSkill] = React.useState(false);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const recurrenceSummary = React.useMemo(
    () =>
      describeRecurrence(
        form.occurrences,
        form.frequency_interval,
        form.frequency_period,
      ),
    [form.occurrences, form.frequency_interval, form.frequency_period],
  );

  // Helper to load jobs for a specific company
  const loadJobsForCompany = async (companyId) => {
    if (!companyId) {
      setAllJobs([]);
      return;
    }
    try {
      const jobsRes = await getJobsByCompanyId(companyId).catch(() => []);
      setAllJobs(Array.isArray(jobsRes) ? jobsRes : []);
    } catch {
      setAllJobs([]);
    }
  };

  // Reload only the task details (does not refetch companies/jobs)
  const getActiveTaskId = React.useCallback(() => {
    if (routeTaskId) {
      try {
        localStorage.setItem("activeTaskId", String(routeTaskId));
      } catch {}
      return routeTaskId;
    }
    try {
      return localStorage.getItem("activeTaskId");
    } catch {
      return null;
    }
  }, [routeTaskId]);

  const didInitRef = React.useRef(false);
  React.useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    (async () => {
      setLoading(true);
      try {
        const companiesRes = await getCompanyNames().catch(() => []);
        setCompanies(Array.isArray(companiesRes) ? companiesRes : []);
        const [catsRes, statusRes] = await Promise.all([
          getTaskCategories().catch(() => []),
          getTaskStatuses().catch(() => []),
        ]);
        setTaskCategories(Array.isArray(catsRes) ? catsRes : []);
        setTaskStatuses(Array.isArray(statusRes) ? statusRes : []);
        // Don't set all jobs here - will be loaded based on task's company below

        // Load task details
        const id = getActiveTaskId();
        if (!id) throw new Error("No active task selected");
        const res = await getTaskWithRelations(id);
        setData(res);

        // Load jobs for the task's company
        const taskCompanyId = res?.task_company_id ?? res?.company_id ?? "";
        const companyJobs =
          taskCompanyId ?
            await getJobsByCompanyId(taskCompanyId).catch(() => [])
          : [];
        // Jobs already assigned to this task can belong to a different company
        // than the task itself, so they won't appear in the company-filtered
        // pool. Merge them in so their RACI rows stay visible/editable instead
        // of silently vanishing (while still counting toward validation).
        const assignedJobs = (Array.isArray(res?.jobTasks) ? res.jobTasks : [])
          .map((jt) => jt?.job)
          .filter(Boolean);
        const mergedJobs = Array.isArray(companyJobs) ? [...companyJobs] : [];
        const seenJobIds = new Set(mergedJobs.map((j) => String(j?.job_id)));
        for (const j of assignedJobs) {
          const key = String(j?.job_id);
          if (!seenJobIds.has(key)) {
            mergedJobs.push(j);
            seenJobIds.add(key);
          }
        }
        setAllJobs(mergedJobs);

        const base = {
          task_name: res?.task_name || "",
          task_code: res?.task_code || "",
          version: res?.task_version || res?.version || "",
          task_company_id: res?.task_company_id ?? res?.company_id ?? "",
          expected_process_time: res?.expected_process_time ?? "",
          optimistic_process_time: res?.optimistic_process_time ?? "",
          pessimistic_process_time: res?.pessimistic_process_time ?? "",
          expected_waiting_time: res?.expected_waiting_time ?? "",
          expected_rework_time: res?.expected_rework_time ?? "",
          status_id:
            res?.status_id ?? res?.task_status_id ?? res?.task_status?.id ?? "",
          category_id:
            res?.category_id ??
            res?.task_category_id ??
            res?.task_category?.id ??
            "",
          task_overview: res?.task_overview || "",
          frequency_period: res?.frequency_period || "WEEK",
          frequency_interval:
            res?.frequency_interval != null ?
              String(res.frequency_interval)
            : "1",
          occurrences:
            res?.occurrences != null ? String(res.occurrences) : "1",
          skills: (Array.isArray(res?.taskSkills) ? res.taskSkills : []).map(
            (ts) => ({
              name: ts?.skill?.name || ts?.skill_name || "",
              level: ts?.level?.level_name || "",
            }),
          ),
        };
        setForm(base);

        // The job-task join rows carry the RACI role (null if linked but
        // not yet assigned a role)
        const taskJobTasks = Array.isArray(res?.jobTasks) ? res.jobTasks : [];
        const currentRaciRows = taskJobTasks
          .map((jt) => ({
            job_id: jt?.job_id ?? jt?.job?.job_id,
            role: jt?.role ?? null,
            time_allocation_percentage:
              jt?.time_allocation_percentage != null
                ? Number(jt.time_allocation_percentage)
                : null,
          }))
          .filter((r) => r.job_id != null);

        // Coming from "unassigned entities" with a job to link to this task
        const jobToAddId = localStorage.getItem("jobToAddToTask");
        if (jobToAddId) {
          const jobIdNum = Number(jobToAddId);
          if (!currentRaciRows.some((r) => r.job_id === jobIdNum)) {
            currentRaciRows.push({ job_id: jobIdNum, role: null });
          }
          localStorage.removeItem("jobToAddToTask");
        }
        setRaciRows(currentRaciRows);
      } catch (e) {
        toast.error(e?.message || "Failed to load task");
      } finally {
        setLoading(false);
      }
    })();
  }, [getActiveTaskId]);

  // Backend only enforces the RACI rule when `raci` is sent non-empty; mirror
  // that here so the button disables in lockstep with what the save will do.
  const raciValid = raciRows.length === 0 || getRaciValidation(raciRows).valid;

  const taskOverviewText = String(form.task_overview || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
  const processTimeValue = Number(form.expected_process_time);
  const processTimeValid =
    form.expected_process_time !== "" &&
    Number.isFinite(processTimeValue) &&
    processTimeValue >= 0;

  const canSave =
    raciValid &&
    String(form.task_name).trim() &&
    String(form.task_code).trim() &&
    String(form.task_company_id) &&
    taskOverviewText.length >= 10 &&
    processTimeValid;

  const navigateAfterTaskAction = () => {
    const taskReturnTo = localStorage.getItem("taskReturnTo");
    const taskReturnJobId = localStorage.getItem("taskReturnJobId");
    if (taskReturnTo === "job-detail" && taskReturnJobId) {
      localStorage.removeItem("taskReturnTo");
      localStorage.removeItem("taskReturnJobId");
      localStorage.removeItem("jobDetailsActiveTab");
      setActiveSection("job-detail", { jobId: taskReturnJobId });
      return;
    }
    if (taskReturnTo === "job-workload") {
      localStorage.removeItem("taskReturnTo");
      localStorage.removeItem("taskReturnJobId");
      localStorage.removeItem("jobDetailsActiveTab");
      setActiveSection("job-workload");
      return;
    }

    const returnTo = localStorage.getItem("returnTo");
    if (returnTo === "unassigned-entities") {
      const returnToTab = localStorage.getItem("returnToTab");
      if (returnToTab) {
        localStorage.setItem("unassignedEntitiesActiveTab", returnToTab);
        localStorage.removeItem("returnToTab");
      }
      localStorage.removeItem("returnTo");
      setActiveSection("unassigned-entities");
      return;
    }

    setActiveSection("task-management");
  };

  const onBack = () => {
    navigateAfterTaskAction();
  };

  const onSave = async () => {
    if (!canSave || saving || !data?.task_id) return;
    try {
      setSaving(true);
      await updateTask(data.task_id, {
        task_name: form.task_name,
        task_code: form.task_code,
        version: form.version === "" ? 0 : Number(form.version),
        task_company_id:
          form.task_company_id === "" ? null : Number(form.task_company_id),
        expected_process_time:
          form.expected_process_time === "" ? null : Number(form.expected_process_time),
        optimistic_process_time:
          form.optimistic_process_time === "" ? null : Number(form.optimistic_process_time),
        pessimistic_process_time:
          form.pessimistic_process_time === "" ? null : Number(form.pessimistic_process_time),
        expected_waiting_time:
          form.expected_waiting_time === "" ? null : Number(form.expected_waiting_time),
        expected_rework_time:
          form.expected_rework_time === "" ? null : Number(form.expected_rework_time),
        status_id: form.status_id === "" ? null : Number(form.status_id),
        category_id: form.category_id === "" ? null : Number(form.category_id),
        task_overview: form.task_overview,
        frequency_period: form.frequency_period || "WEEK",
        frequency_interval:
          form.frequency_interval === "" ? 1 : Number(form.frequency_interval),
        occurrences: form.occurrences === "" ? 1 : Number(form.occurrences),
        raci: raciRows,
        // Backend expects taskSkills for patch; do not send 'skills' alongside
        taskSkills: (form.skills || [])
          .filter((s) => s.name && s.level)
          .map((s) => ({ skill_name: s.name, level: s.level })),
      });
      // Navigate back after successful save
      toast.success(`Task "${form.task_name}" has been updated successfully!`);
      navigateAfterTaskAction();
    } catch (e) {
      const message = e?.message || "Failed to save changes";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Edit Task</h2>
            <p className="page-subtitle">
              {form.task_name ?
                `Editing: ${form.task_name}`
              : "Update task details, required skills, and RACI assignments"}
              {form.task_code ? ` (${form.task_code})` : ""}
            </p>
          </div>
          <div className="roles-toolbar">
            <button className="secondary-btn" onClick={onBack}>
              Back
            </button>
            <button
              className="primary-btn"
              onClick={onSave}
              disabled={!canSave || saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {loading ? (
          <div className="no-results">Loading...</div>
        ) : (
          <SideTabs
            defaultActiveId="basic"
            tabs={[
              {
                id: "basic",
                label: "Basic Details",
                content: (
                  <div className="role-card">
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Task Name *</label>
                        <input
                          value={form.task_name}
                          onChange={(e) =>
                            setField("task_name", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>Task Code *</label>
                        <input
                          value={form.task_code}
                          onChange={(e) =>
                            setField("task_code", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>Version</label>
                        <input
                          value={form.version ?? ""}
                          disabled
                          style={{
                            backgroundColor: "var(--gray-50)",
                            cursor: "not-allowed",
                            color: "var(--gray-500)",
                          }}
                          placeholder="0"
                        />
                      </div>
                      <div className="form-group">
                        <label>Expected Process Time (min) *</label>
                        <input
                          type="number"
                          min={0}
                          value={form.expected_process_time}
                          onChange={(e) =>
                            setField("expected_process_time", e.target.value)
                          }
                        />
                        {!processTimeValid && (
                          <div
                            className="input-hint"
                            style={{
                              color: "var(--red-ink-700)",
                              fontSize: 12,
                              marginTop: 4,
                            }}
                          >
                            Expected process time is required
                          </div>
                        )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="task-optimistic-time">Optimistic Process Time (min)</label>
                        <input
                          id="task-optimistic-time"
                          type="number"
                          min={0}
                          value={form.optimistic_process_time}
                          onChange={(e) => setField("optimistic_process_time", e.target.value)}
                          placeholder="best case — optional"
                        />
                        <small style={{ color: "var(--gray-500)" }}>
                          Best-case duration. With the pessimistic value it feeds the Monte-Carlo
                          simulation; empty = ±20% assumption.
                        </small>
                      </div>
                      <div className="form-group">
                        <label htmlFor="task-pessimistic-time">Pessimistic Process Time (min)</label>
                        <input
                          id="task-pessimistic-time"
                          type="number"
                          min={0}
                          value={form.pessimistic_process_time}
                          onChange={(e) => setField("pessimistic_process_time", e.target.value)}
                          placeholder="worst case — optional"
                        />
                        {form.optimistic_process_time !== "" && form.pessimistic_process_time !== "" && form.expected_process_time !== "" &&
                          !(Number(form.optimistic_process_time) <= Number(form.expected_process_time) && Number(form.expected_process_time) <= Number(form.pessimistic_process_time)) && (
                          <div className="input-hint" style={{ color: "var(--red-ink-700)", fontSize: 12, marginTop: 4 }}>
                            Must satisfy optimistic ≤ expected ≤ pessimistic
                          </div>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Expected Waiting Time (min)</label>
                        <input
                          type="number"
                          min={0}
                          value={form.expected_waiting_time}
                          onChange={(e) =>
                            setField("expected_waiting_time", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>Expected Rework Time (min)</label>
                        <input
                          type="number"
                          min={0}
                          value={form.expected_rework_time}
                          onChange={(e) =>
                            setField("expected_rework_time", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>Task Status</label>
                        <select
                          value={form.status_id}
                          onChange={(e) =>
                            setField("status_id", e.target.value)
                          }
                        >
                          <option value="">Select Status</option>
                          {taskStatuses.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.description || `Status ${s.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Task Category</label>
                        <select
                          value={form.category_id}
                          onChange={(e) =>
                            setField("category_id", e.target.value)
                          }
                        >
                          <option value="">Select Category</option>
                          {taskCategories.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.description || `Category ${c.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Company *</label>
                        <select
                          value={form.task_company_id}
                          onChange={(e) => {
                            const val = e.target.value;
                            setField("task_company_id", val);
                            loadJobsForCompany(val);
                            // Reset RACI matrix when company changes (job pool changes)
                            setRaciRows([]);
                          }}
                        >
                          <option value="">Select Company</option>
                          {companies.map((c) => (
                            <option
                              key={c.company_id || c.id}
                              value={c.company_id || c.id}
                            >
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label title="Total number of times this task should occur within the recurrence window">
                          Occurrences
                        </label>
                        <input
                          type="number"
                          min={1}
                          step={1}
                          value={form.occurrences}
                          onChange={(e) =>
                            setField("occurrences", e.target.value)
                          }
                          placeholder="e.g. 2"
                        />
                        <small className="hint">
                          Number of times this task repeats within one
                          recurrence window
                        </small>
                      </div>
                      <div className="form-group">
                        <label title="How many periods to wait between occurrences (e.g. 3 means every 3 weeks)">
                          Repeat Interval
                        </label>
                        <input
                          type="number"
                          min={1}
                          step={1}
                          value={form.frequency_interval}
                          onChange={(e) =>
                            setField("frequency_interval", e.target.value)
                          }
                          placeholder="e.g. 3"
                        />
                        <small className="hint">
                          Gap between recurrence windows, counted in the unit
                          set below — e.g. 3 with "Week" means every 3 weeks
                        </small>
                      </div>
                      <div className="form-group">
                        <label title="Unit of time used for the repeat interval (Day, Week, Month, or Year)">
                          Period
                        </label>
                        <select
                          value={form.frequency_period}
                          onChange={(e) =>
                            setField("frequency_period", e.target.value)
                          }
                        >
                          {FREQUENCY_PERIODS.map((p) => (
                            <option key={p} value={p}>
                              {p}
                            </option>
                          ))}
                        </select>
                        <small className="hint">
                          Time unit the repeat interval is measured in
                        </small>
                      </div>
                      <div className="form-group full">
                        <label>Recurrence Summary</label>
                        <input value={recurrenceSummary} disabled />
                      </div>
                      <div className="form-group full">
                        <label>Associated Processes</label>
                        <div className="chip-list">
                          <span className="muted">
                            Processes are managed elsewhere and are read-only
                            here.
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                id: "description",
                label: "Description",
                content: (
                  <div className="role-card">
                    <RichTextEditor
                      label="Task Description *"
                      value={form.task_overview}
                      onChange={(val) => setField("task_overview", val)}
                      height={280}
                    />
                    {taskOverviewText.length < 10 && (
                      <div
                        className="input-hint"
                        style={{ color: "var(--red-ink-700)", fontSize: 12, marginTop: 4 }}
                      >
                        Description must be at least 10 characters
                      </div>
                    )}
                  </div>
                ),
              },
              {
                id: "skills",
                label: "Req Skills",
                content: (
                  <div className="role-card">
                    <div
                      className="section-title"
                      style={{
                        marginBottom: 12,
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>Req Skills</span>
                      <button
                        type="button"
                        className="primary-btn icon-square"
                        onClick={() => setShowAddSkill(true)}
                        aria-label="Add Skill"
                        title="Add Skill"
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path
                            d="M12 5v14M5 12h14"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </div>
                    <SkillTable
                      mode="manage"
                      skills={form.skills}
                      levels={LEVELS}
                      onDelete={(idx) =>
                        setForm((prev) => ({
                          ...prev,
                          skills: prev.skills.filter((_, i) => i !== idx),
                        }))
                      }
                    />
                    <SkillAddModal
                      open={showAddSkill}
                      onClose={() => setShowAddSkill(false)}
                      levels={LEVELS}
                      onSave={(s) => {
                        setForm((prev) => ({
                          ...prev,
                          skills: [...prev.skills, s],
                        }));
                        setShowAddSkill(false);
                      }}
                    />
                  </div>
                ),
              },
              {
                id: "raci",
                label: "RACI",
                content: (
                  <RaciMatrix
                    mode="manage"
                    jobs={allJobs}
                    rows={raciRows}
                    onChange={setRaciRows}
                    label={form.task_name}
                    taskId={routeTaskId}
                  />
                ),
              },
            ]}
          />
        )}
      </div>
    </div>
  );
};

export default TaskEditPage;
